import { useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { webColors } from '@liminal/brand/semantic';
import { borderRadius } from '@liminal/brand/scale';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { Link } from '@liminal/web-ui/primitives/Link';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { PageHeader } from '@liminal/web-ui/display/PageHeader';
import { DetailRow } from '@liminal/web-ui/display/DetailRow';
import { CopyableId } from '@liminal/web-ui/display/CopyableId';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { ErrorState } from '@liminal/web-ui/feedback/ErrorState';
import { LoadingSpinner } from '@liminal/web-ui/feedback/LoadingSpinner';
import { Skeleton } from '@liminal/web-ui/feedback/Skeleton';
import { Modal } from '@liminal/web-ui/overlays/Modal';
import { Textarea } from '@liminal/web-ui/forms/Textarea';
import { FormErrorBanner } from '@liminal/web-ui/forms/FormErrorBanner';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { formatOrderDate } from '@liminal/utils/date';
import { isHttpError } from '@liminal/errors/http-errors';
import { payOptionsFor } from '@liminal/content/rewards';
import { MerchantIdentity_Type, RewardBudget_State, RewardMerchant_State } from '@/api/generated';
import type { RewardMerchant } from '@/api/types';
import { getErrorMessage } from '@/errors/messages';
import {
  useChangeRewardMerchantState,
  useRewardBudget,
  useRewardMerchant,
  useRewardMerchantRevisions,
} from '@/hooks/useRewards';
import { budgetState, merchantCategory, merchantState } from '@/components/ui/statusHelpers';
import { useNow } from '@/hooks/useNow';
import {
  cashbackPercent,
  formatCnpj,
  groupDecimal,
  merchantCategoryOf,
  parseResourceId,
  previewRates,
  usdText,
} from '@/lib/rewards';
import { BudgetUsage } from '../components/BudgetUsage';
import { AvailabilityNotice, CatalogueCards } from '../components/CatalogueDetails';
import { MerchantGallery } from '../components/MerchantGallery';

const LOGO_SIZE = 56;

const REVISION_COLUMNS = ['When', 'By', 'Pay with LIL', 'Earn', 'Levels', 'Status', 'Why', 'Revision'] as const;

/** The payment the rates are shown against: the design's example. */
const EXAMPLE_USD = '20.40';
const IDENTITY_LABELS: Record<MerchantIdentity_Type, string> = {
  [MerchantIdentity_Type.TYPE_CNPJ]: 'CNPJ',
  [MerchantIdentity_Type.TYPE_PIX_KEY]: 'Pix key',
};

type StateChange = 'pause' | 'resume' | 'retire';

/** The state changes open from each state. Retiring is for good. */
const CHANGES: Record<RewardMerchant_State, readonly StateChange[]> = {
  [RewardMerchant_State.STATE_ACTIVE]: ['pause', 'retire'],
  [RewardMerchant_State.STATE_PAUSED]: ['resume', 'retire'],
  [RewardMerchant_State.STATE_RETIRED]: [],
};

/** What each state means for the partner's customers. */
const STATE_MEANING: Record<RewardMerchant_State, string> = {
  [RewardMerchant_State.STATE_ACTIVE]: 'Customers paying it by Pix can earn LIL or pay with LIL.',
  [RewardMerchant_State.STATE_PAUSED]: 'Paused: its payments still match, but customers cannot choose until it resumes.',
  [RewardMerchant_State.STATE_RETIRED]: 'Retired for good: its payments no longer match.',
};

const CHANGE_COPY: Record<StateChange, { label: string; title: string; detail: string }> = {
  pause: {
    label: 'Pause',
    title: 'Pause this partner',
    detail: 'Its payments still open, but customers cannot choose Pay with LIL or Earn on them until it resumes.',
  },
  resume: { label: 'Resume', title: 'Resume this partner', detail: 'Customers can choose on its payments again.' },
  retire: {
    label: 'Retire',
    title: 'Retire this partner',
    detail: 'For good: its identities are freed and its payments no longer match. This cannot be undone.',
  },
};

export function PartnerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const merchantId = id ?? '';
  const merchant = useRewardMerchant(merchantId);

  if (merchant.isLoading) return <LoadingSpinner />;
  if (merchant.isError || !merchant.data) {
    return <ErrorState message={getErrorMessage(merchant.error)} onRetry={() => void merchant.refetch()} />;
  }
  return <PartnerDetail merchantId={merchantId} merchant={merchant.data} />;
}

function PartnerDetail({ merchantId, merchant }: { readonly merchantId: string; readonly merchant: RewardMerchant }) {
  const navigate = useNavigate();
  const state = merchantState(merchant.state);
  const budgetId = merchant.budget ? parseResourceId(merchant.budget, 'rewardBudgets') : null;
  const budget = useRewardBudget(budgetId ?? '');
  const [change, setChange] = useState<StateChange | null>(null);
  const [reason, setReason] = useState('');
  const [changeError, setChangeError] = useState('');
  // One idempotency key per opening of the dialog, kept across a retry.
  const [changeRequestId, setChangeRequestId] = useState('');
  const changeState = useChangeRewardMerchantState();
  // Windows open and close while the page is up; a minute is fine-grained enough.
  const now = useNow(60_000);
  const category = merchantCategoryOf(merchant.category);
  const pagination = useCursorPagination(50);
  const revisions = useRewardMerchantRevisions(merchantId, pagination.pageToken ?? '');
  const revisionRows = revisions.data?.rewardMerchants ?? [];
  const nextPageToken = revisions.data?.nextPageToken ?? '';
  if (merchant.state === undefined) throw new Error(`merchant ${merchant.name} has no state`);
  const changes = CHANGES[merchant.state];
  const rates = merchant.rates;
  const cashback = rates ? cashbackPercent(rates.earnLilPerUsd, rates.redeemLilPerUsd) : null;
  const example = rates ? previewRates(EXAMPLE_USD, rates.redeemLilPerUsd, rates.earnLilPerUsd) : null;
  // The Pay with LIL levels, priced by the server. With them, Pay with LIL is
  // one of the levels the example payment offers, never the whole payment.
  const levels = (rates?.payLevels ?? []).map((level, index) => {
    if (!level.lil) throw new Error(`merchant ${merchant.name}'s pay level ${index + 1} has no LIL`);
    return { usd: level.usd, lil: level.lil };
  });
  const exampleOffers = payOptionsFor(EXAMPLE_USD, levels).map(
    (option) => `${groupDecimal(option.lil)} LIL to get ${usdText(option.usdBack)} back`,
  );

  const openChange = (next: StateChange) => {
    setChangeRequestId(crypto.randomUUID());
    setChange(next);
  };
  const closeChange = () => {
    setChange(null);
    setReason('');
    setChangeError('');
  };

  const confirmChange = async () => {
    if (!change) return;
    if (reason.trim() === '') {
      setChangeError('Say why.');
      return;
    }
    if (!merchant.etag) throw new Error(`merchant ${merchant.name} has no etag`);
    try {
      await changeState.mutateAsync({ id: merchantId, change, etag: merchant.etag, reason: reason.trim(), requestId: changeRequestId });
      closeChange();
    } catch (e) {
      setChangeError(
        isHttpError(e) && e.status === 409
          ? 'This partner was changed by someone else. Close this, reload and try again.'
          : getErrorMessage(e),
      );
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Button variant="link" size="sm" startIcon="arrow-back" onClick={() => navigate('/rewards/partners')}>
          Partners
        </Button>
      </Box>

      <Stack direction="row" spacing={2} alignItems="flex-start">
        {merchant.logoUri ? (
          <Box
            component="img"
            src={merchant.logoUri}
            alt={`${merchant.displayName ?? 'Partner'} logo`}
            sx={{
              width: LOGO_SIZE,
              height: LOGO_SIZE,
              flexShrink: 0,
              objectFit: 'contain',
              borderRadius: `${borderRadius.md}px`,
              border: `1px solid ${webColors.border.light}`,
              bgcolor: webColors.surface.primary,
            }}
          />
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <PageHeader
            title={merchant.displayName ?? merchantId}
            autoTitleCase={false}
            subtitle={STATE_MEANING[merchant.state]}
            actions={
              <Stack direction="row" spacing={1}>
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/rewards/payments?${new URLSearchParams({ merchant: merchant.name })}`)}
                >
                  Payments
                </Button>
                {changes.map((c) => (
                  <Button key={c} variant={c === 'retire' ? 'danger' : 'secondary'} onClick={() => openChange(c)}>
                    {CHANGE_COPY[c].label}
                  </Button>
                ))}
                {merchant.state !== RewardMerchant_State.STATE_RETIRED && (
                  <Button startIcon="pencil" onClick={() => navigate(`/rewards/partners/${merchantId}/edit`)}>
                    Edit
                  </Button>
                )}
              </Stack>
            }
          />
        </Box>
      </Stack>
      <AvailabilityNotice merchant={merchant} now={now} />

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, gap: 3, alignItems: 'start' }}>
        <Stack spacing={3}>
          <MerchantGallery images={merchant.images ?? []} />

          <Card variant="elevated">
            <Stack spacing={2}>
              <Typography variant="h6">What customers get</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <DetailRow
                  layout="stacked"
                  label="Pay with LIL"
                  value={rates ? `${groupDecimal(rates.redeemLilPerUsd)} LIL per $1 back` : '—'}
                />
                <DetailRow
                  layout="stacked"
                  label="Or earn"
                  value={rates ? `${groupDecimal(rates.earnLilPerUsd)} LIL per $1 spent` : '—'}
                />
                <DetailRow layout="stacked" label="Cashback" value={cashback === null ? '—' : `${cashback}% in LIL`} />
              </Box>
              <DetailRow
                layout="stacked"
                label="Pay with LIL levels"
                value={
                  levels.length === 0 ? (
                    'None: Pay with LIL covers the whole payment'
                  ) : (
                    <Stack spacing={0.5}>
                      {levels.map((level, index) => (
                        <span key={level.usd}>{`Level ${index + 1}: up to ${usdText(level.usd)} back for ${groupDecimal(level.lil)} LIL`}</span>
                      ))}
                    </Stack>
                  )
                }
              />
              {example && (
                <Typography variant="body2" color="text.secondary">
                  {levels.length === 0
                    ? `On a ${usdText(EXAMPLE_USD)} payment: spend ${groupDecimal(example.redeemLil)} LIL to get ${usdText(EXAMPLE_USD)} back, or earn ${groupDecimal(example.earnLil)} LIL.`
                    : `On a ${usdText(EXAMPLE_USD)} payment: spend ${exampleOffers.join(', ')}, or earn ${groupDecimal(example.earnLil)} LIL.`}
                </Typography>
              )}
            </Stack>
          </Card>

          <Card variant="elevated">
            <Stack spacing={2}>
              <Stack spacing={0.5}>
                <Typography variant="h6">Which payments count</Typography>
                <Typography variant="body2" color="text.secondary">
                  A Pix payment to any of these is this partner&apos;s.
                </Typography>
              </Stack>
              <Stack>
                {(merchant.identities ?? []).map((identity) => {
                  if (identity.type === undefined) throw new Error(`merchant ${merchant.name} has an identity without a type`);
                  const value = identity.value ?? '';
                  return (
                    <Stack
                      key={`${identity.type}:${value}`}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ py: 1, borderTop: `1px solid ${webColors.border.light}` }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ width: 64, flexShrink: 0 }}>
                        {IDENTITY_LABELS[identity.type]}
                      </Typography>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {identity.type === MerchantIdentity_Type.TYPE_CNPJ ? formatCnpj(value) : value}
                      </Typography>
                    </Stack>
                  );
                })}
              </Stack>
            </Stack>
          </Card>

          <CatalogueCards merchant={merchant} now={now} />
        </Stack>

        <Stack spacing={3}>
          <Card variant="elevated">
            <Stack spacing={2}>
              <Typography variant="h6">Budget</Typography>
              {!merchant.budget ? (
                <Typography variant="body2" color="text.secondary">
                  None
                </Typography>
              ) : budgetId === null ? (
                <Typography variant="body2" color="error">
                  {`"${merchant.budget}" is not a budget`}
                </Typography>
              ) : (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">
                      <Link component={RouterLink} to={`/rewards/budgets/${budgetId}`}>
                        {budget.data?.displayName ?? merchant.budget}
                      </Link>
                    </Typography>
                    {budget.data?.state !== undefined && budget.data.state !== RewardBudget_State.STATE_OPEN && (
                      <StatusPill tone={budgetState(budget.data.state).tone} label={budgetState(budget.data.state).label} size="sm" />
                    )}
                  </Stack>
                  {budget.isLoading ? (
                    <Skeleton height={40} />
                  ) : budget.isError ? (
                    <Typography variant="body2" color="error">
                      {`The budget could not be read: ${getErrorMessage(budget.error)}`}
                    </Typography>
                  ) : (
                    <BudgetUsage
                      limitUsd={budget.data?.limitUsd}
                      spentUsd={budget.data?.spentUsd}
                      reservedUsd={budget.data?.reservedUsd}
                    />
                  )}
                </>
              )}
            </Stack>
          </Card>

          <Card variant="elevated">
            <Stack spacing={2}>
              <Typography variant="h6">Details</Typography>
              <DetailRow layout="stacked" label="Status" value={<StatusPill tone={state.tone} label={state.label} size="sm" />} />
              <DetailRow
                layout="stacked"
                label="Category"
                value={
                  category === null ? (
                    'Not set'
                  ) : (
                    <StatusPill tone={merchantCategory(category).tone} label={merchantCategory(category).label} size="sm" />
                  )
                }
              />
              <DetailRow layout="stacked" label="Description" value={merchant.description || '—'} />
              <DetailRow
                layout="stacked"
                label="Last change"
                value={
                  merchant.revisionCreateTime
                    ? `${formatOrderDate(merchant.revisionCreateTime)}${merchant.revisionActor ? ` by ${merchant.revisionActor}` : ''}`
                    : '—'
                }
              />
              {merchant.revisionReason && <DetailRow layout="stacked" label="Why" value={merchant.revisionReason} />}
              <DetailRow layout="stacked" label="Id" value={<CopyableId value={merchantId} />} />
            </Stack>
          </Card>
        </Stack>
      </Box>

      <Stack spacing={2}>
        <Typography variant="h6">Change history</Typography>
        <Card variant="elevated" padding="none">
          <Table>
            <TableHead>
              <TableRow>
                {REVISION_COLUMNS.map((column) => (
                  <TableCell key={column}>{column}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <DataTableBody
              columns={REVISION_COLUMNS.length}
              isLoading={revisions.isLoading}
              isError={revisions.isError}
              isEmpty={revisionRows.length === 0}
              loadingRows={4}
              emptyMessage="No changes."
              errorMessage="Could not load the change history."
            >
              {revisionRows.map((r) => {
                const revisionState = merchantState(r.state);
                return (
                  <TableRow key={r.revisionId}>
                    <TableCell>{r.revisionCreateTime ? formatOrderDate(r.revisionCreateTime) : '—'}</TableCell>
                    <TableCell>{r.revisionActor || '—'}</TableCell>
                    <TableCell>{r.rates ? `${groupDecimal(r.rates.redeemLilPerUsd)} per $1` : '—'}</TableCell>
                    <TableCell>{r.rates ? `${groupDecimal(r.rates.earnLilPerUsd)} per $1` : '—'}</TableCell>
                    <TableCell>
                      {r.rates?.payLevels?.length ? r.rates.payLevels.map((level) => usdText(level.usd)).join(' · ') : '—'}
                    </TableCell>
                    <TableCell>
                      <StatusPill tone={revisionState.tone} label={revisionState.label} size="sm" />
                    </TableCell>
                    <TableCell>{r.revisionReason || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {r.revisionId}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </DataTableBody>
          </Table>
        </Card>
        <CursorPagination
          firstRowNumber={pagination.firstRowNumber}
          count={revisionRows.length}
          hasPreviousPage={pagination.hasPreviousPage}
          hasNextPage={!!nextPageToken}
          onPreviousPage={pagination.goToPreviousPage}
          onNextPage={() => pagination.goToNextPage(nextPageToken)}
          disabled={revisions.isLoading}
        />
      </Stack>

      <Modal
        open={change !== null}
        onClose={closeChange}
        title={change ? CHANGE_COPY[change].title : ''}
        dismissible
        actions={
          <Stack direction="row" spacing={2}>
            <Button variant="link" onClick={closeChange} disabled={changeState.isPending}>
              Cancel
            </Button>
            <Button
              variant={change === 'retire' ? 'danger' : 'primary'}
              onClick={() => void confirmChange()}
              loading={changeState.isPending}
            >
              {change ? CHANGE_COPY[change].label : ''}
            </Button>
          </Stack>
        }
      >
        <Stack spacing={2}>
          <Typography variant="body2">{change ? CHANGE_COPY[change].detail : ''}</Typography>
          <Textarea label="Reason" value={reason} onChange={setReason} rows={2} helper="Kept in the change history" required />
          <FormErrorBanner error={changeError} />
        </Stack>
      </Modal>
    </Stack>
  );
}
